/**
 * AntwortVorschlag - Teil 3: the AI reply draft, editable (Konzept 8.2 / 4.8).
 *
 * An editor, not the read-only AssistenzBlock of the reading pane: 8.2 wants the draft
 * editable without a tab switch. Marked "KI-Vorschlag" with its ConfidenceBadge, and
 * "Bearbeitet" once the text differs from the AI's - an edited draft is a human text
 * and should not keep claiming otherwise (Konzept 4.8 original_ai_text).
 *
 * Speichern writes the edit through PUT /tasks/:id/draft, which keeps the original AI
 * text and audits the delta (C3). Zurücksetzen goes back to that original, not to the
 * last save.
 *
 * [Source: docs/eingangsverarbeitung-epic.md §3 WS-C C3]
 */
import { useEffect, useState } from 'react';
import { Check, Copy, RotateCcw, Sparkles } from 'lucide-react';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { KonfidenzBadge } from '../../communication-v2/components/konfidenz';

interface AntwortVorschlagProps {
  /** The KI's own text — the `original_ai_text` of Konzept 4.8, once that lands. */
  draft: string | null;
  /** The KI-Zusammenfassung, shown above the draft when the assist produced one. */
  summary: string | null;
  /** Resolved 0–100 (assist confidence, else the task konfidenz). */
  konfidenz: number;
  /**
   * The task the draft belongs to. Changing it re-seeds the editor: T3 follows the T2
   * selection (4.5), so a draft must never bleed from one Aufgabe into the next.
   */
  taskId: string | null;
  /** S3: request a KI-Vorschlag on demand when none exists yet (POST assist/refresh). */
  onGenerate?: () => void;
  /** S3: a generate request is in flight. */
  generating?: boolean;
  /** S3: the last generate error, shown under the button. */
  genError?: string | null;
  /** The KI's untouched text, for Zurücksetzen. Defaults to `draft`. */
  originalText?: string | null;
  /** C3: persist the edit. Absent = read-only editor (mock mode). */
  onSave?: (text: string) => void;
  saving?: boolean;
  saveError?: string | null;
}

export function AntwortVorschlag({
  draft,
  summary,
  konfidenz,
  taskId,
  onGenerate,
  generating = false,
  genError = null,
  originalText = null,
  onSave,
  saving = false,
  saveError = null,
}: AntwortVorschlagProps) {
  const [text, setText] = useState(draft ?? '');

  /* Re-seed on a new task OR a newly-arrived draft. Keyed on both so a late-resolving
     assist for the SAME task still fills an untouched editor, while switching tasks
     always resets — the selection owns the draft, not the component's history. */
  useEffect(() => {
    setText(draft ?? '');
  }, [taskId, draft]);

  const original = originalText ?? draft;
  const edited = draft !== null && text !== draft;
  const abweichend = original !== null && text !== original;

  return (
    <section aria-label="KI-Antwortvorschlag" className="flex flex-col gap-3">
      {summary && (
        <div className="flex flex-col gap-1.5">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            KI-Zusammenfassung
          </h4>
          <p className="rounded-md border border-border/70 bg-muted/30 p-3 text-sm leading-relaxed text-muted-foreground">
            {summary}
          </p>
        </div>
      )}

      {draft !== null && (
        <div className="flex flex-col gap-1.5">
          {/* Kennzeichnung als AI-Vorschlag + ConfidenceBadge (8.2), on the draft. */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="info" className="gap-1">
              <Sparkles className="size-3" />
              KI-Vorschlag
            </Badge>
            <KonfidenzBadge value={konfidenz} />
            {edited && <Badge variant="neutral">Bearbeitet</Badge>}
          </div>

          <textarea
            aria-label="Antwortentwurf"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            className="w-full resize-y rounded-md border border-border bg-background p-3 text-sm leading-relaxed text-foreground shadow-inner focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />

          <div className="flex items-center justify-end gap-2">
            {saveError && <p className="mr-auto text-xs text-sem-alert">{saveError}</p>}
            {abweichend && original !== null && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setText(original)}
                className="text-muted-foreground"
              >
                <RotateCcw className="size-4" />
                Zurücksetzen
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void navigator.clipboard?.writeText(text)}
              className="text-muted-foreground"
            >
              <Copy className="size-4" />
              Kopieren
            </Button>
            {onSave && (
              <Button size="sm" disabled={!edited || saving} onClick={() => onSave(text)}>
                <Check className="size-4" />
                {saving ? 'Wird gespeichert …' : 'Speichern'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* S3: no draft yet → offer to generate one on demand (POST assist/refresh). Shown
          even when only a summary exists (M1). Without a generate handler, keep the plain note. */}
      {draft === null &&
        (onGenerate ? (
          <div className="flex flex-col items-start gap-2">
            <p className="text-sm text-muted-foreground">
              Für diese Aufgabe gibt es noch keinen KI-Vorschlag.
            </p>
            <Button size="sm" onClick={onGenerate} disabled={generating}>
              <Sparkles className="size-4" />
              {generating ? 'Wird generiert …' : 'KI-Vorschlag generieren'}
            </Button>
            {genError && <p className="text-xs text-sem-alert">{genError}</p>}
          </div>
        ) : (
          !summary && (
            <p className="text-sm text-muted-foreground">
              Für diese Aufgabe gibt es keinen Vorschlag.
            </p>
          )
        ))}
    </section>
  );
}
